import React, { useState, useEffect } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { CheckCircle, XCircle, AlertCircle } from "lucide-react";

export default function CompanyConnectStripePage() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(true);

  // Check current Stripe connection status on mount
  useEffect(() => {
    checkStripeStatus();
  }, []);

  const checkStripeStatus = async () => {
    setChecking(true);
    try {
      const res = await fetchWithAuth("/api/contracts/organization-stripe-status/");
      setStatus(res);
    } catch (err) {
      console.error("Failed to check Stripe status:", err);
    } finally {
      setChecking(false);
    }
  };

  const handleConnect = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth("/api/contracts/create-org-stripe-account/", { 
        method: "POST" 
      });
      
      if (res.url) {
        // Redirect to Stripe onboarding
        window.location.href = res.url;
      } else {
        alert("Unable to start Stripe onboarding.");
      }
    } catch (err) {
      console.error("Stripe onboarding error:", err);
      alert("Failed to connect to Stripe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleManagePayouts = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth("/api/contracts/stripe-dashboard-link/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account: "organization" }),
      });
      if (res.url) {
        window.location.href = res.url;
      } else {
        alert(res.error || "Couldn't open Stripe. Please try again.");
      }
    } catch (err) {
      alert(err?.message || "Couldn't open Stripe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    // Just call connect again - it will create a new onboarding link
    handleConnect();
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-bg text-ink flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-tertiary">Checking Stripe connection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-ink flex flex-col items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        <h1 className="text-4xl font-display italic text-ink mb-4 text-center">Stripe Connect Setup</h1>
        <p className="text-secondary text-center mb-8">
          Connect your organization to Stripe to receive and make payments.
        </p>

        {/* Connection Status Card */}
        {status?.connected && (
          <div className="bg-surface border border-line rounded-2xl p-6 mb-6">
            <h2 className="text-xl font-semibold text-ink mb-4 flex items-center gap-2">
              {status.charges_enabled ? (
                <CheckCircle className="text-success-text" size={24} />
              ) : (
                <AlertCircle className="text-gold-ink" size={24} />
              )}
              Connection Status
            </h2>

            <div className="space-y-3">
              <StatusItem 
                label="Stripe Account Connected" 
                enabled={status.connected}
              />
              <StatusItem 
                label="Can Accept Payments" 
                enabled={status.charges_enabled}
              />
              <StatusItem 
                label="Payouts Enabled" 
                enabled={status.payouts_enabled}
              />
              <StatusItem 
                label="Setup Complete" 
                enabled={status.charges_enabled && status.payouts_enabled}
              />
            </div>

            {!status.charges_enabled && (
              <div className="mt-6 p-4 bg-[#FBF3E2] border border-line rounded-lg">
                <p className="text-gold-ink text-sm">
                  ⚠️ Your Stripe account is not fully set up. Complete the onboarding process to start receiving and making payments.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-4">
          {!status?.connected ? (
            <button
              onClick={handleConnect}
              disabled={loading}
              className="w-full bg-brand hover:bg-brand-hover px-6 py-4 rounded-pill text-white text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Redirecting to Stripe..." : "Connect Stripe Account"}
            </button>
          ) : !status.charges_enabled ? (
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="w-full bg-brand hover:bg-brand-hover px-6 py-4 rounded-pill text-white text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Redirecting..." : "Complete Stripe Setup"}
            </button>
          ) : (
            <div className="text-center p-6 bg-[#E7F2EC] border border-line rounded-2xl">
              <CheckCircle className="text-success-text mx-auto mb-3" size={48} />
              <h3 className="text-xl font-semibold text-success-text mb-2">
                All Set! 🎉
              </h3>
              <p className="text-secondary">
                Your organization is connected to Stripe and ready to receive and make payments.
              </p>
            </div>
          )}

          {status?.connected && status.charges_enabled && (
            <button
              onClick={handleManagePayouts}
              disabled={loading}
              className="w-full bg-brand hover:bg-brand-hover px-6 py-4 rounded-pill text-white text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Opening Stripe..." : "Manage payouts"}
            </button>
          )}

          <button
            onClick={checkStripeStatus}
            className="w-full border border-line-strong bg-surface text-ink hover:border-brand hover:text-brand px-6 py-3 rounded-pill transition-colors"
          >
            Refresh Status
          </button>
        </div>

        {/* Info Section */}
        <div className="mt-8 p-6 bg-surface border border-line rounded-2xl">
          <h3 className="font-semibold text-ink mb-3 flex items-center gap-2">
            <AlertCircle size={20} className="text-brand" />
            What is Stripe Connect?
          </h3>
          <p className="text-sm text-tertiary leading-relaxed">
            Stripe Connect allows your organization to receive and make payments directly from clients for work completed by your contractors. 
            Payments are securely processed through Stripe and deposited into your organization's bank account.
          </p>
        </div>
      </div>
    </div>
  );
}

// Helper component for status items
function StatusItem({ label, enabled }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-secondary">{label}</span>
      {enabled ? (
        <CheckCircle className="text-success-text" size={20} />
      ) : (
        <XCircle className="text-faint" size={20} />
      )}
    </div>
  );
}